import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getClass, updateClass } from '../services/classes';
import { useLocations } from '../hooks/useLocations';
import { ClassType, CreateClassInput } from '../types/models';

const CLASS_TYPES: ClassType[] = ['Beginner', 'Intermediate', 'Advanced', 'SUP Yoga'];

const EMPTY: CreateClassInput = {
  title: '',
  publicDescription: '',
  detailedDescription: '',
  classType: 'Beginner',
  startDateTime: '',
  duration: 60,
  locationId: '',
  physicalAddress: { street: '', city: '', state: '', zip: '' },
  capacity: 20,
};

function toDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const offset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 16);
}

export const EditClassPage: React.FC = () => {
  const navigate = useNavigate();
  const { classId } = useParams<{ classId: string }>();
  const { locations } = useLocations();
  const [form, setForm] = useState<CreateClassInput>(EMPTY);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!classId) return;
    getClass(classId)
      .then(cls => {
        setForm({
          title: cls.title,
          publicDescription: cls.publicDescription,
          detailedDescription: cls.detailedDescription ?? '',
          classType: cls.classType,
          startDateTime: toDatetimeLocal(cls.startDateTime),
          duration: cls.duration,
          locationId: cls.locationId ?? '',
          physicalAddress: cls.physicalAddress ?? { street: '', city: '', state: '', zip: '' },
          capacity: cls.capacity,
        });
      })
      .catch(err => setLoadError((err as Error).message))
      .finally(() => setLoading(false));
  }, [classId]);

  const set = (patch: Partial<CreateClassInput>) => setForm(f => ({ ...f, ...patch }));
  const setAddr = (patch: Partial<CreateClassInput['physicalAddress']>) =>
    set({ physicalAddress: { ...form.physicalAddress, ...patch } });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...form,
        startDateTime: form.startDateTime ? new Date(form.startDateTime).toISOString() : '',
      };
      await updateClass(classId!, payload);
      navigate(`/classes/${classId}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500';
  const label = 'block text-sm font-medium text-gray-700 mb-1';

  if (loading) return <div className="text-center py-20 text-gray-500">Loading…</div>;
  if (loadError) return <div className="max-w-2xl mx-auto px-4 py-8 text-red-600">{loadError}</div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <Link to={`/classes/${classId}`} className="text-sm text-green-600 hover:text-green-700 mb-4 inline-block">
        ← Back to Class
      </Link>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Edit Class</h1>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <div>
          <label className={label}>Title</label>
          <input className={input} required value={form.title} onChange={e => set({ title: e.target.value })} />
        </div>

        <div>
          <label className={label}>Public Description</label>
          <textarea className={input} rows={3} required value={form.publicDescription}
            onChange={e => set({ publicDescription: e.target.value })} />
        </div>

        <div>
          <label className={label}>Detailed Description (visible after registration)</label>
          <textarea className={input} rows={3} value={form.detailedDescription}
            onChange={e => set({ detailedDescription: e.target.value })} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Class Type</label>
            <select className={input} value={form.classType} onChange={e => set({ classType: e.target.value as ClassType })}>
              {CLASS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Capacity</label>
            <input type="number" className={input} min={1} max={500} required value={form.capacity}
              onChange={e => set({ capacity: parseInt(e.target.value) })} />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Date &amp; Time</label>
            <input
              type="datetime-local"
              className={input}
              required
              value={form.startDateTime}
              onChange={e => set({ startDateTime: e.target.value })}
            />
          </div>
          <div>
            <label className={label}>Duration (minutes)</label>
            <input type="number" className={input} min={15} max={300} required value={form.duration}
              onChange={e => set({ duration: parseInt(e.target.value) })} />
          </div>
        </div>

        <div>
          <label className={label}>Location</label>
          <select className={input} required value={form.locationId} onChange={e => set({ locationId: e.target.value })}>
            <option value="">Select a location…</option>
            {locations.map(l => <option key={l.locationId} value={l.locationId}>{l.displayName}</option>)}
          </select>
        </div>

        <fieldset className="border border-gray-200 rounded-lg p-4">
          <legend className="text-sm font-medium text-gray-700 px-1">Address</legend>
          <div className="space-y-3 mt-2">
            <input className={input} placeholder="Street" required value={form.physicalAddress.street}
              onChange={e => setAddr({ street: e.target.value })} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input className={input} placeholder="City" required value={form.physicalAddress.city}
                onChange={e => setAddr({ city: e.target.value })} />
              <input className={input} placeholder="State" required value={form.physicalAddress.state}
                onChange={e => setAddr({ state: e.target.value })} />
              <input className={input} placeholder="ZIP" required value={form.physicalAddress.zip}
                onChange={e => setAddr({ zip: e.target.value })} />
            </div>
          </div>
        </fieldset>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-600 text-sm">{error}</div>
        )}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
          <Link
            to={`/classes/${classId}`}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
};
